<template>
  <tr class="bg-gray-50 hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-16 last-child:pr-3 text-sm">
    <td class="py-3 text-left">
      <div class="flex items-start space-x-2">
        <span class="font-semibold text-gray-900">{{ sharedFund.code }}</span>

        <p class="w-60 text-gray-700">
          {{ sharedFund.name }}
        </p>
      </div>
    </td>

    <td class="pr-3">
      {{ australianCurrency(sharedFund.budget) }}
    </td>

    <td class="bg-green-200/50">
      {{ sharedFundCost?.current_claim != null ? australianCurrency(sharedFundCost.current_claim) : '' }}
    </td>

    <td class="bg-green-200/50">
      {{ sharedFundCost?.current_certified != null ? australianCurrency(sharedFundCost.current_certified) : '' }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(previousAmount) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ australianCurrency(cumulativeAmount) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages && sharedFund.budget && sharedFundCost?.current_claim != null">
        {{ australianNumber((sharedFundCost.current_claim / sharedFund.budget) * 100) }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      <span v-if="sharedFund.budget && sharedFundCost?.current_certified != null">
        {{ australianNumber((sharedFundCost.current_certified / sharedFund.budget) * 100) }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ sharedFund.budget ? australianNumber((previousAmount / sharedFund.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ sharedFund.budget ? australianNumber((cumulativeAmount / sharedFund.budget) * 100) : 0 }}%
    </td>

    <td :class="{ 'text-red-600': hasVariance && variance !== 0 }">
      {{ hasVariance ? australianCurrency(variance) : '' }}
    </td>

    <td class="pr-3">
      {{ australianCurrency(sharedFund.remaining(claim, currentCertified)) }}
    </td>
  </tr>
</template>

<script setup lang="ts">
import { australianCurrency, australianNumber, falsey } from '@/helpers';
import Claim from '@/models/Claim';
import SharedFund from '@/models/SharedFund';
import { useSharedFundCostsStore } from '@/store/sharedFundCosts';
import { computed } from 'vue';

const props = defineProps<{
  sharedFund: SharedFund;
  claim: Claim;
  showPercentages?: boolean;
}>();

const sharedFundCostsStore = useSharedFundCostsStore();

const sharedFundCost = computed(() => {
  return sharedFundCostsStore.models
    .where('shared_fund_id', props.sharedFund.id)
    .where('claim_id', props.claim.id)
    .first();
});

const previousAmount = computed(() => props.sharedFund.previous(props.claim));
const currentCertified = computed(() => sharedFundCost.value?.current_certified ?? 0);
const cumulativeAmount = computed(() => previousAmount.value + currentCertified.value);

const variance = computed(() => {
  return (sharedFundCost.value?.current_claim ?? 0) - (sharedFundCost.value?.current_certified ?? 0);
});

const hasVariance = computed(() => {
  return !falsey(sharedFundCost.value?.current_claim) && !falsey(sharedFundCost.value?.current_certified);
});
</script>

<style scoped></style>
